
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FloatingButtons } from "@/components/common/FloatingButtons";
import { Calculator, TrendingUp, PiggyBank, Home, Car } from "lucide-react";
import { UniversalCalculator } from "@/components/calculators/UniversalCalculator";
import { ApplyNowModal } from "@/components/common/ApplyNowModal";

const Calculators = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeCalculator, setActiveCalculator] = useState("Personal Loan EMI");
  const [activeTab, setActiveTab] = useState("loan-calculators");
  const calculatorSectionRef = useRef<HTMLDivElement>(null);

  const handleCalculatorChange = (calculator: string) => {
    setActiveCalculator(calculator);
    // Scroll to calculator section after a small delay to ensure the UI has updated
    setTimeout(() => {
      calculatorSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  useEffect(() => {
    // Check for navigation state when component mounts
    if (location.state?.activeCalculator) {
      const calculator = location.state.activeCalculator;
      setActiveCalculator(calculator);
      
      // Set the active tab based on the calculator type
      const category = calculatorCategories.find(cat => 
        cat.calculators.includes(calculator)
      );
      if (category) {
        setActiveTab(category.id);
      }
      
      // Clear the navigation state to prevent reapplying on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const calculatorCategories = [
    {
      id: "loan-calculators",
      title: "Loan Calculators",
      icon: Calculator,
      calculators: [
        "Personal Loan EMI", "Home Loan EMI", "Car Loan EMI", "Business Loan EMI",
        "Education Loan EMI", "Gold Loan EMI", "Two Wheeler Loan", "Mortgage EMI"
      ]
    },
    {
      id: "bank-specific",
      title: "Bank-Specific EMI",
      icon: TrendingUp,
      calculators: [
        "HDFC Bank EMI", "SBI EMI", "ICICI Bank EMI", "Axis Bank EMI",
        "Kotak Bank EMI", "BOI EMI", "Canara Bank EMI", "PNB EMI", "IndusInd EMI", "Other Bank EMI"
      ]
    },
    {
      id: "investment",
      title: "Investment Calculators",
      icon: PiggyBank,
      calculators: [
        "SIP Calculator", "CAGR Calculator", "Compound Interest", "Simple Interest",
        "PPF Calculator", "ELSS Calculator", "Sukanya Samriddhi", "EPF Calculator"
      ]
    },
    {
      id: "tax-calculators",
      title: "Tax Calculators",
      icon: Home,
      calculators: [
        "Income Tax Calculator", "HRA Calculator", "TDS Calculator",
        "Salary Calculator", "Stamp Duty Calculator", "GST Calculator"
      ]
    },
    {
      id: "special-calculators",
      title: "Special Calculators",
      icon: Car,
      calculators: [
        "Flat vs Reducing Rate", "Prepayment Calculator", "Loan to Value",
        "APR Calculator", "Retirement Calculator", "Gratuity Calculator", "PMAY Calculator"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-20">
      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Financial Calculators
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            Calculate EMIs, plan investments, and make informed financial decisions with our comprehensive calculator suite
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 pb-20">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Calculator Categories */}
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 bg-slate-800/50 backdrop-blur-lg mb-8">
            {calculatorCategories.map((category) => (
              <TabsTrigger
                key={category.id}
                value={category.id}
                className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              >
                <category.icon className="h-4 w-4 mr-2" />
                <span className="hidden md:inline">{category.title}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Calculator Content */}
          {calculatorCategories.map((category) => (
            <TabsContent key={category.id} value={category.id}>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {category.calculators.map((calc, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className={`h-auto p-4 border-slate-600 text-white hover:bg-slate-700/50 ${
                      activeCalculator === calc ? "bg-blue-600/20 border-blue-500" : ""
                    }`}
                    onClick={() => handleCalculatorChange(calc)}
                  >
                    <div className="text-center">
                      <Calculator className="h-5 w-5 mx-auto mb-2" />
                      <div className="text-sm font-medium">{calc}</div>
                    </div>
                  </Button>
                ))}
              </div>

              {/* Active Calculator */}
              <div ref={calculatorSectionRef} className="bg-slate-800/30 backdrop-blur-lg rounded-2xl p-8 border border-slate-700">
                <UniversalCalculator calculatorType={activeCalculator} />
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Quick Access Calculators */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-white text-center mb-8">Quick Access Calculators</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Home Loan EMI",
                description: "Calculate your monthly home loan EMI",
                icon: Home,
                color: "from-green-500 to-blue-500",
                type: "Home Loan EMI"
              },
              {
                title: "SIP Calculator",
                description: "Plan your systematic investment",
                icon: TrendingUp,
                color: "from-purple-500 to-pink-500",
                type: "SIP Calculator"
              },
              {
                title: "Car Loan EMI",
                description: "Calculate your car loan EMI",
                icon: Car,
                color: "from-orange-500 to-red-500",
                type: "Car Loan EMI"
              }
            ].map((calc, index) => (
              <Card key={index} className="bg-slate-800/50 backdrop-blur-lg border-slate-700 hover:bg-slate-700/50 transition-all duration-300 hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className={`mx-auto mb-4 p-3 bg-gradient-to-r ${calc.color} rounded-full w-fit`}>
                    <calc.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{calc.title}</h3>
                  <p className="text-slate-300 mb-4">{calc.description}</p>
                  <Button 
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    onClick={() => handleCalculatorChange(calc.type)}
                  >
                    Calculate Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-lg border-slate-700 max-w-2xl mx-auto">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Apply for a Loan?</h3>
              <p className="text-slate-300 mb-6">Get instant approval with our AI-powered loan processing</p>
              <ApplyNowModal>
                <Button className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-lg px-8 py-3">
                  Apply Now - Get Instant Approval
                </Button>
              </ApplyNowModal>
            </CardContent>
          </Card>
        </div>
      </div>

      <FloatingButtons />
    </div>
  );
};

export default Calculators;
